import { z } from "zod";


export const medicalSchema = z.object({
	id: z.string(),
	name: z.string(),
	dosage: z.string(),
	quantity: z.number(),
	supply_duration: z.string(),
});

export type Medical = z.infer<typeof medicalSchema>;

export const medicalFormSchema = z.object({
	name: z.string().min(1, "Name is required"),
	email: z.string().email("Invalid email address"),
	phone: z.string().min(1, "Phone number is required"),
	notes: z.string().optional(),
});

export const medicationFormSchema = z.object({
	name: z.string().min(1, "Name is required"),
	email: z.string().email("Invalid email address"),
	phone: z.string().min(1, "Phone number is required"),
	notes: z.string().optional(),
});

export const medicationConfirmationSearchParamsSchema = z.object({
	prescription_id: z.string().optional(),
	refill_id: z.string().optional(),
});

export type MedicationConfirmationSearchParams = z.infer<typeof medicationConfirmationSearchParamsSchema>;

export const medicationFormUpdateSchema = medicationFormSchema.partial().extend({
	id: z.string(),
});

export type MedicalFormData = z.infer<typeof medicalFormSchema>;

export const medicalFormUpdateSchema = medicalFormSchema.partial().extend({
	id: z.string(),
});
export type MedicalFormUpdateData = z.infer<typeof medicalFormUpdateSchema>;

export const conditionFormSchema = z.object({
	name: z.string().min(1, "Condition name is required"),
	date_diagnosed: z.string().min(1, "Diagnosis date is required"),
	medication: z.string().min(1, "Treatment details are required").optional(),
	is_treated: z.boolean().default(false).optional(),
})

export const conditionFormUpdateSchema = conditionFormSchema.and(z.object({
	id: z.string(),
}));

export type ConditionVariables = z.infer<typeof conditionFormSchema>;
export type ConditionUpdateVariables = z.infer<typeof conditionFormUpdateSchema>;

export const allergyFormSchema = z.object({
	name: z.string().min(1, "Allergy name is required"),
	severity: z.string().min(1, "Severity is required"),
});

export const allergyFormUpdateSchema = allergyFormSchema.partial().extend({
	id: z.string(),
});

export const deliveryAddressSchema = z.object({
	street: z.string().min(1, "Street Address is required"),
	city: z.string().min(1, "City is required"),
	state: z.string().optional(),
	zip_code: z.string().optional(),
});

export const medicationDeliveryDetailsSchema = z.object({
	prescription_id: z.string(),
	delivery_address: deliveryAddressSchema,
	success_url: z.string().min(1, "Success URL is required").optional(),
	cancel_url: z.string().min(1, "Cancel URL is required").optional(),
});

export const medicationRefillPaymentDetailsSchema = z.object({
	prescription_id: z.string(),
	medication_id: z.string(),
	delivery_address: deliveryAddressSchema,
	success_url: z.string().min(1, "Success URL is required").optional(),
	cancel_url: z.string().min(1, "Cancel URL is required").optional(),
});

export const updateNotificationReminderSchema = z.object({
	field: z.enum([
		"refill_reminder",
		"daily_reminder",
	]),
	value: z.boolean()
});

export const sideEffectsSchema = z.object({
	nausea: z.boolean(),
	diarrhea: z.boolean(),
	constipation: z.boolean(),
	headache: z.boolean(),
	other_symptoms: z.string(),
	none: z.boolean().optional(),
});



export const refillRequestSchema = z.object({
	side_effects: z.object({ ...sideEffectsSchema.shape })
});

export type SideEffects = z.infer<typeof sideEffectsSchema>;
export type SideEffectsDataSchema = z.infer<typeof refillRequestSchema>;

export type MedicationDeliveryDetails = z.infer<typeof medicationDeliveryDetailsSchema>;
export type MedicationPaymentAddress = z.infer<typeof deliveryAddressSchema>;


export type MedicationRefillPaymentDetails = z.infer<typeof medicationRefillPaymentDetailsSchema>;



export type AllergyVariables = z.infer<typeof allergyFormSchema>;
export type AllergyUpdateVariables = z.infer<typeof allergyFormUpdateSchema>;
